import { RefObject, useContext, useMemo } from 'react'

import { EDITOR_TOOLS } from '@editor/config/constants'
import { TOAST_CONFIG } from '@editor/config/styles'
import { EditorContext } from '@editor/contexts/Editor'
import { PointArray, ShapeKey, ShapeKeyType, ShapeTypes } from '@editor/interfaces/interfaces'
import { Tools } from '@editor/pages/projects/editor/tools/setup'
import { createRayCaster, getClickCoords } from '@editor/services/Points'
import { validateSelectedShape } from '@editor/services/Util'
import { getVolumeEstimationItems } from '@editor/services/VolumeEstimation'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { setHoveredElementId } from '@editor/store/tool'
import { toaster } from '@editor/system/toaster'
import { ThreeEvent } from '@react-three/fiber'
import { Camera } from 'three'

export const TOAST_ID = 'shape-selection-error'

// User need to click down an up on the same mesh to make a selection.
// Store the down status on this state object for the up status checking
let isPointerDown = false

const useShape = (shape: ShapeTypes, shapeKey: ShapeKey | ShapeKeyType, cameraRef: RefObject<Camera | null>) => {
  // Context
  const { changeSelectedShapeIds } = useContext(EditorContext)

  // Store
  const dispatch = useAppDispatch()
  const inspectionItems = useAppSelector((state) => state.inspectionItems.items)
  const shapes = useAppSelector((state) => state.shapes.shapes)
  const selectedElementIds = useAppSelector((root) => root.toolUtil.core.selectedElementIds)
  const hoveredElementId = useAppSelector((root) => root.toolUtil.core.hoveredElementId)
  const isDragging = useAppSelector((state) => state.toolUtil.core.isDragging)
  const selectedTool = useAppSelector((state) => state.editor.selectedTool)

  // Current Editor tool
  const currentEditorTool = Tools.find((tool) => tool.key === selectedTool)

  /**
   * A selectable shape is based on the current editor tool configuration of either:
   * 1. In the whitelist of selectable shapes
   * 2. Belongs to an inspection item that is in the whitelist of selectable inspection items
   */
  const isShapeSelectable = useMemo(() => {
    // can be selected through shape whitelist
    const byShape = currentEditorTool?.config?.selectableShapes?.includes(shapeKey as ShapeKeyType) || false
    if (byShape) return true

    // check through inspection item type whitelist
    const items = inspectionItems.filter(
      (it) => it.shape_ids.polygons?.includes(shape.shape_id) || it.shape_ids.cylinders?.includes(shape.shape_id),
    )
    if (items.length === 0) return false

    return items.some((item) =>
      item?.item_type ? currentEditorTool?.config?.selectableInspectionItems?.includes(item.item_type) : false,
    )
  }, [currentEditorTool, inspectionItems, shape, shapeKey])

  const getSelectedPoint = (event: ThreeEvent<PointerEvent>): PointArray | undefined => {
    if (!cameraRef.current) return undefined

    //* カメラ取得
    const camera = cameraRef.current

    //* 光線生成とクリック位置特定
    const rayCaster = createRayCaster()
    const targetElem = (event.nativeEvent.target as HTMLElement).getBoundingClientRect()
    const { x, y, width, height } = targetElem
    const { coords } = getClickCoords(
      {
        x: event.nativeEvent.clientX,
        y: event.nativeEvent.clientY,
      },
      {
        x,
        y,
        width,
        height,
      },
    )

    //* カメラからpointまで光線を伸ばす
    rayCaster.setFromCamera(coords, camera)
    const intersects = rayCaster.intersectObject(event.eventObject)
    if (!intersects.length || intersects[0].point === undefined) {
      return undefined
    }

    return intersects[0].point.toArray()
  }

  /**
   * Pointer over object event handler
   * @param event
   * @returns
   */
  const onPointerOver = (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation()

    if ((selectedTool !== EDITOR_TOOLS.MOVE && !isShapeSelectable) || isDragging) {
      return
    }
    dispatch(setHoveredElementId(shape.shape_id))
  }

  /**
   * Pointer out event handler
   * @param event
   * @returns
   */
  const onPointerOut = (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation()

    if ((selectedTool !== EDITOR_TOOLS.MOVE && !isShapeSelectable) || isDragging) {
      return
    }
    dispatch(setHoveredElementId(shape.shape_id))
    dispatch(setHoveredElementId(''))
    isPointerDown = false
  }

  /**
   * Pointer down event handler
   */
  const onPointerDown = () => {
    isPointerDown = true
  }

  /**
   * Pointer up event handler
   * @param event
   */
  const onPointerUp = (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation()
    if (isPointerDown) {
      onClick(event)
    }
    isPointerDown = false
  }

  /**
   * Handle clicking of a shape
   *
   * @param event Click event
   */
  const onClick = (event: ThreeEvent<PointerEvent>) => {
    if (selectedTool === EDITOR_TOOLS.COMMENT) {
      const anchorPoint = getSelectedPoint(event)
      if (!anchorPoint || !cameraRef) return
    }

    if ((selectedTool !== EDITOR_TOOLS.MOVE && !isShapeSelectable) || isDragging) {
      return
    }

    try {
      validateSelectedShape(shape, shapeKey, getVolumeEstimationItems(inspectionItems), currentEditorTool)
    } catch (e) {
      if (!toaster.isVisible(TOAST_ID)) {
        toaster.create({
          ...TOAST_CONFIG,
          id: TOAST_ID,
          type: 'error',
          title: e instanceof Error ? e.message : 'エラー',
        })
      }
      return
    }

    let newShapeIds = [...selectedElementIds]

    // If only 1 volume can be selected, deselect all other volumes
    if (currentEditorTool && currentEditorTool.config?.volume?.onlyOneSelectable && shapeKey === ShapeKeyType.POLYGON) {
      newShapeIds = newShapeIds
        .filter((id) => !shapes.polygons.find((poly) => poly.shape_id === id))
        .filter((id) => !shapes.cylinders.find((cylinder) => cylinder.shape_id === id))
    }

    const idIndex = newShapeIds.indexOf(shape.shape_id)
    if (idIndex >= 0) {
      newShapeIds.splice(idIndex, 1)
    } else {
      newShapeIds.push(shape.shape_id)
    }

    changeSelectedShapeIds(newShapeIds)
  }

  return {
    onPointerOver,
    onPointerOut,
    onPointerDown,
    onPointerUp,
    isHovered: shape.shape_id === hoveredElementId,
  }
}

export default useShape
